import type { PermissionVocabularyViewModel } from './api';

/**
 * Human-readable rendering of a five-segment grammar permission (PTV2-1443), driven by the vocabulary
 * so nothing is hardcoded. A chip shows the compact label; the raw wire string stays available as the
 * chip's tooltip so the exact grammar is never hidden.
 */

const WILDCARD = '*';
const SEGMENT_COUNT = 5;

/** The grammar slot each segment belongs to, in wire order. */
const SLOT_BY_INDEX = ['entityGroup', 'entity', 'featureGroup', 'feature', 'action'] as const;

/** Split a permission into its five segments, or `null` if it is not exactly five. */
export function splitPermission(raw: string): readonly string[] | null {
  const parts = raw.split(':');
  return parts.length === SEGMENT_COUNT ? parts : null;
}

/** Fallback humanization when the vocabulary has no label for a value: `manage-owner` → `Manage owner`. */
function humanize(value: string): string {
  const spaced = value.replace(/[_-]/g, ' ');
  return spaced.length === 0 ? spaced : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function termLabel(
  vocabulary: PermissionVocabularyViewModel | undefined,
  slotName: string,
  value: string,
): string {
  const slot = vocabulary?.slots.find((s) => s.name === slotName);
  const term = slot?.values.find((t) => t.value === value);
  return term?.label ?? humanize(value);
}

/**
 * A compact label for a permission chip, e.g. `*:patient:*:*:view` → "Patient · View",
 * `patients:*:*:*:create` → "Patients · Create". Only the non-wildcard segments are shown, each
 * labelled via the vocabulary (falling back to a humanized value). A malformed string, or one with no
 * concrete segment (all wildcards), renders as the raw string so nothing is silently lost.
 */
export function formatPermissionLabel(
  raw: string,
  vocabulary?: PermissionVocabularyViewModel,
): string {
  const parts = splitPermission(raw);
  if (parts === null) {
    return raw;
  }
  const labelled = parts
    .map((segment, index) =>
      segment === WILDCARD ? null : termLabel(vocabulary, SLOT_BY_INDEX[index] ?? '', segment),
    )
    .filter((label): label is string => label !== null);
  return labelled.length === 0 ? raw : labelled.join(' · ');
}
